// Create/Edit Account Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { crmAPI, userAPI } from '../../services/api';
import { useOnboarding } from '../../context/OnboardingContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Save, Building2, User } from 'lucide-react';

const industryRolesPerson = [
  { value: 'owner', label: 'Owner' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'agent', label: 'Agent' },
  { value: 'farm_manager', label: 'Farm Manager' },
  { value: 'vet', label: 'Veterinarian' },
  { value: 'consignor', label: 'Consignor' },
  { value: 'syndicate_member', label: 'Syndicate Member' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

const industryRolesOrg = [
  { value: 'farm', label: 'Farm' },
  { value: 'farm_boarding', label: 'Boarding Farm' },
  { value: 'veterinary_care', label: 'Veterinary Practice' },
  { value: 'consignment', label: 'Consignment' },
  { value: 'stable', label: 'Stable' },
  { value: 'syndicate', label: 'Syndicate' },
  { value: 'transport', label: 'Transport' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

const prefixes = ['Mr.', 'Mrs.', 'Miss', 'Ms.', 'Mx.', 'Dr.'];

const AccountFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const isContactForm = location.pathname.includes('/contacts');
  const { completeItem } = useOnboarding();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    crm_class: isContactForm ? 'contact' : 'account',
    crm_type: 'organization',
    // Organization fields
    organization_name: '',
    organization_dba: '',
    organization_website: '',
    organization_industry_role: '',
    // Person fields
    person_prefix: '',
    person_first_name: '',
    person_last_name: '',
    person_suffix: '',
    person_preferred_name: '',
    person_salutation: '',
    person_industry_role: '',
    person_birthday: '',
    // Common fields
    contact_preferences: '',
    timezone: '',
    sales_preferences: '',
    other_notes: '',
    account_manager_id: '',
    account_admin_id: '',
    account_active: true,
    // Goals
    overall_goals: [],
    breeding_goal_race: false,
    breeding_goal_sell: false,
    number_of_mares_current: '',
    number_of_mares_goal: '',
    // Financial
    commission_rate_percent: '',
    minimum_commission_amount: '',
    withdrawal_fee_shipin: '',
    withdrawal_fee_sale: '',
    expense_markup_percent: '',
    other_financial_notes: '',
  });

  useEffect(() => {
    fetchUsers();
    if (isEdit) {
      fetchAccount();
    }
  }, [id]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.list();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const fetchAccount = async () => {
    setLoading(true);
    try {
      const response = await crmAPI.get(id);
      setFormData({ ...formData, ...response.data });
    } catch (error) {
      toast.error('Failed to load account');
      navigate('/crm/accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGoalToggle = (goal) => {
    setFormData((prev) => {
      const goals = prev.overall_goals || [];
      if (goals.includes(goal)) {
        return { ...prev, overall_goals: goals.filter((g) => g !== goal) };
      }
      return { ...prev, overall_goals: [...goals, goal] };
    });
  };

  const validateForm = () => {
    // 1. Organization Requirements
    if (formData.crm_type === 'organization') {
      if (!formData.organization_name) {
        toast.error('Organization Name is required');
        return false;
      }
      if (!formData.organization_industry_role) {
        toast.error('Industry Role is required');
        return false;
      }
    }
    
    // 2. Person Requirements
    if (formData.crm_type === 'person') {
      if (!formData.person_first_name) {
        toast.error('First Name is required');
        return false;
      }
      if (!formData.person_last_name) {
        toast.error('Last Name is required');
        return false;
      }
      if (!formData.person_industry_role) {
        toast.error('Industry Role is required');
        return false;
      }
    }

    // 3. Common Requirements - Account Manager only required for Accounts
    if (!isContactForm && !formData.account_manager_id) {
      toast.error('Account Manager is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);

    try {
      // Prepare data
      const data = { ...formData };
      
      // Clean up industry role fields based on type
      if (data.crm_type === 'organization') {
        // For organizations, remove person_industry_role
        delete data.person_industry_role;
      } else {
        // For persons, remove organization_industry_role
        delete data.organization_industry_role;
      }
      
      // Convert empty strings to null for numeric fields
      ['commission_rate_percent', 'minimum_commission_amount', 'withdrawal_fee_shipin', 
       'withdrawal_fee_sale', 'expense_markup_percent', 'number_of_mares_current', 
       'number_of_mares_goal'].forEach(field => {
        if (data[field] === '') data[field] = null;
        else if (data[field]) data[field] = parseFloat(data[field]);
      });

      if (isEdit) {
        await crmAPI.update(id, data);
        toast.success(isContactForm ? 'Contact updated successfully' : 'Account updated successfully');
      } else {
        await crmAPI.create(data);
        toast.success(isContactForm ? 'Contact created successfully' : 'Account created successfully');
        if (!isEdit) completeItem('add_contact');
      }
      navigate(isContactForm ? '/crm/contacts' : '/crm/accounts');
    } catch (error) {
      console.error('Failed to save:', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error(`Failed to save ${isContactForm ? 'contact' : 'account'}`);
        }
      } else {
        toast.error(`Failed to save ${isContactForm ? 'contact' : 'account'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SidebarLayout>
    );
  }

  // Define Basic Info Content as a reusable component or render function
  const renderBasicInfoContent = () => (
    <Card>
      <CardContent className="pt-6 space-y-6">
        {formData.crm_type === 'organization' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization Name *</Label>
                <Input
                  id="organization_name"
                  value={formData.organization_name}
                  onChange={(e) => handleChange('organization_name', e.target.value)}
                  required
                  data-testid="org-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization_dba">DBA (Doing Business As)</Label>
                <Input
                  id="organization_dba"
                  value={formData.organization_dba}
                  onChange={(e) => handleChange('organization_dba', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization_industry_role">Industry Role *</Label>
                <Select
                  value={formData.organization_industry_role}
                  onValueChange={(v) => handleChange('organization_industry_role', v)}
                  required
                >
                  <SelectTrigger data-testid="org-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {industryRolesOrg.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="organization_website">Website</Label>
                <Input
                  id="organization_website"
                  value={formData.organization_website}
                  onChange={(e) => handleChange('organization_website', e.target.value)}
                  placeholder="https://"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="person_prefix">Prefix</Label>
                <Select
                  value={formData.person_prefix}
                  onValueChange={(v) => handleChange('person_prefix', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {prefixes.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="person_first_name">First Name *</Label>
                <Input
                  id="person_first_name"
                  value={formData.person_first_name}
                  onChange={(e) => handleChange('person_first_name', e.target.value)}
                  required={formData.crm_type === 'person'}
                  data-testid="person-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="person_last_name">Last Name *</Label>
                <Input
                  id="person_last_name"
                  value={formData.person_last_name}
                  onChange={(e) => handleChange('person_last_name', e.target.value)}
                  required={formData.crm_type === 'person'}
                  data-testid="person-lastname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="person_suffix">Suffix</Label>
                <Input
                  id="person_suffix"
                  value={formData.person_suffix}
                  onChange={(e) => handleChange('person_suffix', e.target.value)}
                  placeholder="Jr., Sr., III"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="person_industry_role">Industry Role *</Label>
                <Select
                  value={formData.person_industry_role}
                  onValueChange={(v) => handleChange('person_industry_role', v)}
                  required={formData.crm_type === 'person'}
                >
                  <SelectTrigger data-testid="person-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {industryRolesPerson.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="person_preferred_name">Preferred Name</Label>
                <Input
                  id="person_preferred_name"
                  value={formData.person_preferred_name}
                  onChange={(e) => handleChange('person_preferred_name', e.target.value)}
                  placeholder="Nickname"
                />
              </div>
              {/* Birthday field removed for setup as requested */}
            </div>
          </>
        )}

        {/* Account Management - Only for Accounts */}
        {!isContactForm && (
          <>
            <div className="border-t pt-4">
              <h3 className="font-medium mb-4">Account Management</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_manager_id">Account Manager *</Label>
                  <Select
                    value={formData.account_manager_id}
                    onValueChange={(v) => handleChange('account_manager_id', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name} ${user.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_admin_id">Account Admin</Label>
                  <Select
                    value={formData.account_admin_id}
                    onValueChange={(v) => handleChange('account_admin_id', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select admin" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name} ${user.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="account_active"
                checked={formData.account_active}
                onCheckedChange={(checked) => handleChange('account_active', checked)}
              />
              <Label htmlFor="account_active">Account is active</Label>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderGoalsContent = () => (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div>
          <Label className="mb-3 block">Overall Goals</Label>
          <div className="flex flex-wrap gap-3">
            {['breeding', 'seasons_shares', 'selling', 'partnerships', 'racing'].map((goal) => (
              <Button
                key={goal}
                type="button"
                variant={formData.overall_goals?.includes(goal) ? 'default' : 'outline'}
                className={formData.overall_goals?.includes(goal) ? 'bg-primary' : ''}
                onClick={() => handleGoalToggle(goal)}
              >
                {goal.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </Button>
            ))}
          </div>
        </div>

        {formData.overall_goals?.includes('breeding') && (
          <div className="border-t pt-4">
            <h3 className="font-medium mb-4">Breeding Goals</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="breeding_goal_race"
                    checked={formData.breeding_goal_race}
                    onCheckedChange={(checked) => handleChange('breeding_goal_race', checked)}
                  />
                  <Label htmlFor="breeding_goal_race">Breed to race</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="breeding_goal_sell"
                    checked={formData.breeding_goal_sell}
                    onCheckedChange={(checked) => handleChange('breeding_goal_sell', checked)}
                  />
                  <Label htmlFor="breeding_goal_sell">Breed to sell</Label>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="number_of_mares_current">Current # of Mares</Label>
                  <Input
                    id="number_of_mares_current"
                    type="number"
                    value={formData.number_of_mares_current}
                    onChange={(e) => handleChange('number_of_mares_current', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="number_of_mares_goal">Goal # of Mares</Label>
                  <Input
                    id="number_of_mares_goal"
                    type="number"
                    value={formData.number_of_mares_goal}
                    onChange={(e) => handleChange('number_of_mares_goal', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderFinancialContent = () => (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="commission_rate_percent">Commission Rate (%)</Label>
            <Input
              id="commission_rate_percent"
              type="number"
              step="0.1"
              value={formData.commission_rate_percent}
              onChange={(e) => handleChange('commission_rate_percent', e.target.value)}
              placeholder="5.0"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="minimum_commission_amount">Minimum Commission ($)</Label>
            <Input
              id="minimum_commission_amount"
              type="number"
              value={formData.minimum_commission_amount}
              onChange={(e) => handleChange('minimum_commission_amount', e.target.value)}
              placeholder="1000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expense_markup_percent">Expense Markup (%)</Label>
            <Input
              id="expense_markup_percent"
              type="number"
              step="0.1"
              value={formData.expense_markup_percent}
              onChange={(e) => handleChange('expense_markup_percent', e.target.value)}
              placeholder="10.0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="withdrawal_fee_shipin">Withdrawal Fee (Ship-in) ($)</Label>
            <Input
              id="withdrawal_fee_shipin"
              type="number"
              value={formData.withdrawal_fee_shipin}
              onChange={(e) => handleChange('withdrawal_fee_shipin', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="withdrawal_fee_sale">Withdrawal Fee (Sale) ($)</Label>
            <Input
              id="withdrawal_fee_sale"
              type="number"
              value={formData.withdrawal_fee_sale}
              onChange={(e) => handleChange('withdrawal_fee_sale', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="other_financial_notes">Financial Notes</Label>
          <Textarea
            id="other_financial_notes"
            value={formData.other_financial_notes}
            onChange={(e) => handleChange('other_financial_notes', e.target.value)}
            rows={3}
          />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <SidebarLayout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="account-form">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(isContactForm ? '/crm/contacts' : '/crm/accounts')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-semibold">
                {isEdit ? (isContactForm ? 'Edit Contact' : 'Edit Account') : (isContactForm ? 'New Contact' : 'New Account')}
              </h1>
            </div>
          </div>
          <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-btn">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> {isEdit ? `Update ${isContactForm ? 'Contact' : 'Account'}` : `Save ${isContactForm ? 'Contact' : 'Account'}`}</>
            )}
          </Button>
        </div>

        {/* Type Selection - Always Visible */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-lg">Account Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button
                type="button"
                variant={formData.crm_type === 'organization' ? 'default' : 'outline'}
                className={formData.crm_type === 'organization' ? 'bg-primary' : ''}
                onClick={() => handleChange('crm_type', 'organization')}
                data-testid="type-organization-btn"
              >
                <Building2 className="w-4 h-4 mr-2" /> Organization
              </Button>
              <Button
                type="button"
                variant={formData.crm_type === 'person' ? 'default' : 'outline'}
                className={formData.crm_type === 'person' ? 'bg-primary' : ''}
                onClick={() => handleChange('crm_type', 'person')}
                data-testid="type-person-btn"
              >
                <User className="w-4 h-4 mr-2" /> Person
              </Button>
            </div>
          </CardContent>
        </Card>

        {isEdit ? (
          /* Edit Mode: Show Tabs */
          <Tabs defaultValue="basic" className="space-y-4">
            <TabsList>
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="goals">Goals</TabsTrigger>
              {!isContactForm && <TabsTrigger value="financial">Financial</TabsTrigger>}
            </TabsList>

            <TabsContent value="basic">
              {renderBasicInfoContent()}
            </TabsContent>

            <TabsContent value="goals">
              {renderGoalsContent()}
            </TabsContent>

            {!isContactForm && (
              <TabsContent value="financial">
                {renderFinancialContent()}
              </TabsContent>
            )}
          </Tabs>
        ) : (
          /* Create Mode: Only Basic Info, No Tabs */
          renderBasicInfoContent()
        )}
      </form>
    </SidebarLayout>
  );
};

export default AccountFormPage;
